import { useWindowSize } from '@react-hookz/web';
import { useMedia } from 'react-use';
import twMerge from 'twMerge';

import Breadcrumbs from 'molecules/Breadcrumbs';
import Button from 'molecules/Button';
import Image from 'molecules/Image';
import Rating from 'molecules/Rating';
import Section from 'molecules/Section';

import CardDeck from 'components/CardDeck/variants/CardDeck';
import FeaturedResourceCard from 'components/Cards/FeaturedResourceCard';
import ResourceCard from 'components/Cards/ResourceCard';
import ComponentForm from 'components/Form';
import { buttonContainer, headingContent, headingEyebrow, headingSubhead } from 'components/Heading/Heading.styles';
import {
  containerStyles as containerInnerStyles,
  contentStyles,
  formStyles,
  imageContainerStyles,
  imageStyles,
  leftContentStyles,
  resourcesGridStyles,
  textStyles,
  videoEmbedStyles,
  wrapperStyles,
} from 'components/Hero/Hero.styles';
import { createBreadcrumb } from 'components/Hero/utils/functions';
import {
  primaryResourceCardLayout,
  setResourceCardTagColors,
  showPrimaryResourceCardSummary,
  showResourceCardImage,
  showResourceCardInfoBar,
  showResourceCardSummary,
} from 'components/Hero/utils/resourcesUtils';
import MediaAsset from 'components/MediaAsset';

import { contentfulProps } from 'utils/emptyTypes';
import getBreadcrumbLinks from 'utils/getBreadcrumbLinks';
import getTheme from 'utils/getTheme';
import richTextParser from 'utils/richTextParser/richTextParser';

import screens from 'theme/spacing/screens';
import 'components/Hero/Hero.css';

import type { CardDeckProps } from 'components/CardDeck';
import type { ContentfulAsset, ContentfulComponentHero } from 'graphqlTypes';
import type { BreadcrumbsProps } from 'molecules/Breadcrumbs';
import type { ButtonProps } from 'molecules/Button';
import type { ImageData } from 'molecules/Image';
import type { SectionProps, backgroundType } from 'molecules/Section';
import type { FC } from 'react';
import type { RichText } from 'utils/richTextParser/richTextParser';

export type alignmentType = 'Center Aligned' | 'Left Aligned';
export type UnionHeroProps = Omit<ContentfulComponentHero, 'children'> & SectionProps;
export interface HeroProps extends UnionHeroProps {
  authorImage?: ContentfulAsset;
  contentful_id?: string;
  headingAs?: 'h1' | 'h2';
}

const Hero: FC<HeroProps> = ({
  alignment,
  authorImage,
  background,
  backgroundImage,
  breadcrumbsOverride,
  bottomContent,
  buttons,
  cardDeck,
  className,
  contained,
  containerStyles,
  contentful_id,
  content,
  eyebrowLogo,
  eyebrow,
  eyebrowAs,
  featuredResources,
  hasBreadcrumbs,
  heading,
  headingAs,
  isFullHeightImage,
  padding,
  rating,
  reference,
  sectionIdLink,
}) => {
  const isNotFoundPage = sectionIdLink === 'not-found-hero-section';
  const isTablet = useMedia(`(max-width: ${screens.sm})`, false);
  const { width } = useWindowSize();

  const links = getBreadcrumbLinks();
  const crumbs = breadcrumbsOverride
    ? breadcrumbsOverride.map(item => item && createBreadcrumb(item?.label, item?.link))
    : links.map(link => createBreadcrumb('', link));

  const heroBg = (backgroundImage ? 'gray900' : background) as backgroundType;

  const theme = getTheme(heroBg || 'white');
  const resourceCardTagColors = setResourceCardTagColors(featuredResources?.[0] || null);
  const isListingPage = featuredResources?.length === 1;
  const isResource = featuredResources && featuredResources.length > 0;
  const hasBackgroundImage = backgroundImage && true;
  const containerIsFullHeight = isFullHeightImage || hasBackgroundImage;
  const hasReference = reference || hasBackgroundImage ? true : false;
  const Eyebrow = eyebrowAs || 'p';
  const Heading = headingAs || 'h1';

  return (
    <div>
      <Section
        className={className}
        id={sectionIdLink}
        background={heroBg}
        padding={padding}
        contained={contained}
        fullWidth
        containerClass={`hero-component !max-w-full  ${isFullHeightImage ? 'md:!p-0 xl:!pb-0' : ''} ${
          containerIsFullHeight ? 'md:!px-0 !pb-0 xl:!pb-24 xl:!pt-0' : ''
        } ${hasBreadcrumbs && '!pt-0'} ${containerStyles && containerStyles}`}
        contentfulId={contentful_id}
      >
        <div className={`${twMerge(containerInnerStyles({ fullHeight: containerIsFullHeight }))} !pb-0`}>
          <Breadcrumbs
            visible={hasBreadcrumbs}
            breadcrumbs={crumbs as BreadcrumbsProps['breadcrumbs']}
            isDarkTheme={theme === 'dark'}
            className="w-full"
          />
          <div
            className={`${twMerge(
              wrapperStyles({
                alignment: alignment as alignmentType,
                isNotFoundPage,
                isFullHeightImage,
                hasReference,
              }),
            )}`}
          >
            <div className={twMerge(leftContentStyles({ fullHeight: containerIsFullHeight }))}>
              <div className={twMerge(contentStyles({ alignment: alignment as alignmentType, isNotFoundPage }))}>
                <div className={twMerge(textStyles({ alignment: alignment as alignmentType }))}>
                  {eyebrowLogo && (
                    <Image image={eyebrowLogo as ImageData} alt="eyebrow-logo" className="max-h-[75px]" />
                  )}
                  {eyebrow && <Eyebrow className={twMerge(headingEyebrow({ theme }))}>{eyebrow}</Eyebrow>}
                  {heading && (
                    <div className="flex items-center gap-4">
                      {authorImage && (
                        <Image
                          image={authorImage as ImageData}
                          alt={heading || ''}
                          className="mr-4 size-36 rounded-full"
                        />
                      )}
                      <Heading className={twMerge(headingContent({ theme, size: 'h1', isNotFoundPage }))}>
                        {heading}
                      </Heading>
                    </div>
                  )}
                  {content && (
                    <div
                      className={`${twMerge(headingSubhead({ theme, isNotFoundPage }))} ${isTablet && 'hero-content'}`}
                    >
                      {richTextParser(content as RichText, undefined, theme)}
                    </div>
                  )}
                  {isResource && (
                    <div className="subscribe-form mx-auto flex w-full max-w-md justify-center">
                      <ComponentForm
                        formId="4071ec80-df56-48e2-ac0e-f1fa7e60b2c8"
                        isSubscribe
                        isInline
                        {...contentfulProps}
                      />
                    </div>
                  )}
                  {buttons && (
                    <div className={twMerge(buttonContainer({ isNotFoundPage }))}>
                      {buttons.map(
                        button => button && <Button {...(button as ButtonProps)} key={button.internalName} />,
                      )}
                    </div>
                  )}
                </div>
                {rating && <Rating {...rating} alignment={alignment as alignmentType} className="hidden xl:block" />}
                {bottomContent && bottomContent}
              </div>
            </div>
            {reference && reference?.__typename === 'ContentfulItemMediaAsset' && (
              <div
                className={twMerge(
                  imageContainerStyles({ alignment: alignment as alignmentType, isNotFoundPage, isFullHeightImage }),
                )}
              >
                {reference?.youtubeEmbedLink && <MediaAsset className={twMerge(videoEmbedStyles())} {...reference} />}
                {reference?.image && (
                  <div className="size-full">
                    <MediaAsset {...reference} className={twMerge(imageStyles({ isFullHeightImage }))} />
                  </div>
                )}
              </div>
            )}
            {reference && reference?.__typename === 'ContentfulComponentForm' && (
              <div className={twMerge(formStyles())}>
                <ComponentForm {...reference} sectionID={sectionIdLink} />
              </div>
            )}
            {rating && <Rating {...rating} alignment={alignment as alignmentType} className="xl:hidden" />}
          </div>
          {isListingPage ? (
            <FeaturedResourceCard {...featuredResources[0]} />
          ) : (
            <>
              {featuredResources && featuredResources?.length > 0 && (
                <div className={twMerge(resourcesGridStyles())}>
                  <div>
                    <ResourceCard
                      theme="primary-700"
                      layout={primaryResourceCardLayout(width)}
                      tagBackgroundColor={resourceCardTagColors?.background}
                      tagTextColor={resourceCardTagColors?.color}
                      showSummary={showPrimaryResourceCardSummary(width)}
                      showButton={false}
                      padding="sm"
                      {...featuredResources?.[0]}
                    />
                  </div>
                  <div className="flex flex-col gap-6">
                    {featuredResources?.slice(1).map(resource => {
                      const resourceTagColors = resource && setResourceCardTagColors(resource);

                      return (
                        <ResourceCard
                          key={resource?.id}
                          theme="primary-700"
                          layout="horizontal"
                          tagBackgroundColor={resourceTagColors?.background}
                          tagTextColor={resourceTagColors?.color}
                          showImage={showResourceCardImage(width)}
                          showInfoBar={showResourceCardInfoBar(width)}
                          showSummary={showResourceCardSummary(width)}
                          showButton={false}
                          {...resource}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {backgroundImage && (
          <div className="relative inset-0 aspect-video size-full bg-no-repeat xl:absolute">
            <div className="absolute inset-0 z-10 size-full bg-hero-overlay-mobile xl:bg-hero-overlay" />
            <Image
              image={backgroundImage?.image as ImageData}
              alt={backgroundImage?.title || ''}
              className="size-full object-cover"
            />
          </div>
        )}
        {isResource && <div className="absolute bottom-0 left-[calc(50%-50vw)] h-1/4 w-screen bg-white" />}
      </Section>
      {cardDeck && (
        <div className="z-10 -translate-y-10 px-4">
          <CardDeck {...cardDeck} variant={'Contained' as CardDeckProps['variant']} />
        </div>
      )}
    </div>
  );
};

export default Hero;
